import { createColumnHelper } from "@tanstack/react-table"

import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Person } from "@/types/person"
import { getLocationAbbreviation, getLocationColor } from "@/utils/locationDisplay"
import { formatPersonShortName } from "@/utils/personDisplay"

const columnHelper = createColumnHelper<Person>()

const getStatusClasses = (status: string | null) => {
  switch (status) {
    case "Active":
      return "bg-delivered text-indicators-green border-transparent"
    case "Inactive":
      return "bg-palette-jungleGreen100 text-palette-jungleGreen700 border-transparent"
    case "Banned":
      return "bg-palette-angry100 text-indicators-red border-transparent"
    case "Applied":
      return "bg-palette-honeycomb100 text-palette-honeycomb800 border-transparent"
    default:
      return "bg-palette-jungleGreen000 text-palette-jungleGreen600 border-transparent"
  }
}

export const peopleColumns = [
  columnHelper.accessor((row) => (row.person_id ? formatPersonShortName(row) : ""), {
    id: "person_id",
    header: "Person ID",
    cell: (info) => (
      <span className="font-medium text-palette-jungleGreen900">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => {
      const status = info.getValue()
      return (
        <Badge className={`text-xs pointer-events-none ${getStatusClasses(status)}`}>
          {status || "Unknown"}
        </Badge>
      )
    },
  }),
  columnHelper.accessor((row) => (row.location && row.location.length > 0 ? row.location : null), {
    id: "depot",
    header: "Depot",
    cell: (info) => {
      const locations = info.getValue() as string[] | null
      if (!locations || locations.length === 0) return ""
      return (
        <div className="flex gap-1">
          {locations.map((location) => {
            const abbr = getLocationAbbreviation(location)
            const colors = getLocationColor(abbr)
            return (
              <Badge
                key={location}
                className={`text-xs pointer-events-none ${colors.bg} ${colors.text} border-transparent`}
              >
                {abbr}
              </Badge>
            )
          })}
        </div>
      )
    },
  }),
  columnHelper.accessor("phone_number", {
    header: "Phone",
    cell: (info) => info.getValue() || "",
  }),
  columnHelper.accessor("email", {
    header: "Email",
    cell: (info) => info.getValue() || "",
  }),
  columnHelper.accessor("sheriff_type", {
    header: "Sheriff type",
    cell: (info) => info.getValue() || "",
  }),
  columnHelper.accessor("sheriff_areas", {
    id: "sheriff_area",
    header: "Sheriff areas",
    cell: (info) => {
      const areas = info.getValue()
      if (!areas || areas.length === 0) return ""
      const visible = areas.slice(0, 2)
      const hidden = areas.slice(2)
      return (
        <div className="flex gap-1 items-center">
          <span>{visible.join(", ")}</span>
          {hidden.length > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-palette-jungleGreen400 cursor-default">
                    +{hidden.length}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{hidden.join(", ")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )
    },
  }),
  columnHelper.accessor("sticky_location", {
    header: "Sticky location",
    cell: (info) => info.getValue() || "",
  }),
]
